import {FC} from 'react';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {Button, Link} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';

import {useGetVideos, useGetVideoSchedules, Video} from '@entities/video';
import {MrTable} from '@shared/components';
import {
  entityTableCommonProps,
  ROUTE__CREATE_VIDEO,
  ROUTE__VIDEO_DETAILS,
} from '@shared/constants';
import {useUrlState} from '@shared/hooks';

import {PaginationState} from '@/shared/types';

const localStorageKey = 'videosTable';

const VideoSchedulesCount: FC<{videoId: string}> = ({videoId}) => {
  const {data: total} = useGetVideoSchedules(
    {videoId, page: 1, pageSize: 1},
    {select: data => data.total},
  );

  return <>{total ?? '—'}</>;
};

const columns: MRT_ColumnDef<Video>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    enableSorting: false,
    Cell: ({row}) => {
      const video = row.original;

      return (
        <Link
          component={RouterLink}
          to={ROUTE__VIDEO_DETAILS.replace(':video_id', video.id)}
          state={{video}}
        >
          {video.name}
        </Link>
      );
    },
  },
  {
    accessorKey: 'channel',
    header: 'Channel',
    enableSorting: false,
  },
  {
    accessorKey: 'videoUrl',
    header: 'Video URL',
    enableSorting: false,
    Cell: ({row}) => (
      <Link
        href={row.original.videoUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {row.original.videoUrl}
      </Link>
    ),
  },
  {
    id: 'schedulesCount',
    header: 'Active schedules',
    size: 140,
    enableSorting: false,
    Cell: ({row}) => <VideoSchedulesCount videoId={row.original.id} />,
  },
];

export const VideosPage = () => {
  const navigate = useNavigate();

  const {urlState, setUrl} = useUrlState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const {data, isPending, error} = useGetVideos({
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
  });

  return (
    <MrTable
      error={error}
      columns={columns}
      data={data?.items ?? []}
      renderTopToolbarCustomActions={() => (
        <Button onClick={() => navigate(ROUTE__CREATE_VIDEO)}>
          Create video
        </Button>
      )}
      localStorageKeyForSettings={localStorageKey}
      enableHiding
      enablePagination
      manualPagination
      onPaginationChange={setUrl}
      rowCount={data?.total ?? 0}
      state={{
        isLoading: isPending,
        pagination: urlState,
      }}
      {...entityTableCommonProps}
      enableRowActions={false}
    />
  );
};
